'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle as CheckCircleIcon, Tag as TagIcon } from 'lucide-react';

import { Button, Callout } from '@nutralab/ui';

import { RegisterModal } from '@/app/_modals/RegisterModal';
import { useAuth } from '@/app/_providers/AuthProvider';
import { useModal } from '@/app/_providers/ModalProvider';
import { useSiteConfig } from '@/app/_providers/SiteConfigProvider';
import { ANALYTICS_GOALS } from '@/shared/constants';
import { reachGoal } from '@/shared/utils';

interface RegisterDiscountCardProps {
    /** The prices include a discount; `null` while they're being calculated (a signed-in user sees nothing yet). */
    hasDiscount: boolean | null;
    className?: string;
}

/**
 * Invites a guest to sign up for the user discount; tells a signed-in user that the discount is applied, or that
 * they're registered when the prices have no discount.
 */
export function RegisterDiscountCard({ hasDiscount, className }: RegisterDiscountCardProps): React.ReactElement | null {
    const { t } = useTranslation();
    const { isAuthenticated, isInitializing } = useAuth();
    const { openModal } = useModal();
    const { region } = useSiteConfig();

    if (isInitializing) {
        return null;
    }

    if (isAuthenticated) {
        if (hasDiscount === null) {
            return null;
        }
        return (
            <Callout
                intent="success"
                icon={CheckCircleIcon}
                title={t(hasDiscount ? 'register-discount.applied' : 'register-discount.registered')}
                className={className}
            />
        );
    }

    return (
        <Callout
            intent="accent"
            icon={TagIcon}
            title={t('register-discount.title')}
            actions={
                <Button
                    isFullWidth
                    isMultiline
                    color="accent"
                    size="md"
                    onClick={() => {
                        reachGoal(region.code, ANALYTICS_GOALS.CLICK_REGISTRATION_CART);
                        openModal({ ModalComponent: RegisterModal });
                    }}
                >
                    {t('register-discount.action')}
                </Button>
            }
            className={className}
        />
    );
}
